import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  PromotionSelector,
  PromotionMatch,
  PromotionSearchContextSeed,
} from "@/accounting_detail/PromotionSelector.tsx"
import { useState, useRef, useEffect } from "preact/hooks"
import { Label } from "@/components/ui/label.tsx"
import { bulkCreatePromotionMatch } from "@/api/accounting.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"

interface BulkPromotionMatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedTransactionIds: string[]
  onSuccess?: () => void
  /** Seeds the promotion search filters from the first selected row (customer, retailer, dates). */
  searchContextSeed?: PromotionSearchContextSeed
  /** What the host table calls a row — "transaction" on accounting, "deduction" on the deduction table. */
  itemNoun?: string
}

export function BulkPromotionMatchDialog({
  open,
  onOpenChange,
  selectedTransactionIds,
  onSuccess,
  searchContextSeed,
  itemNoun = "transaction"
}: BulkPromotionMatchDialogProps) {
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const popoverOpenRef = useRef(false)
  
  // Use the first transaction ID for promotion selector (they should all be from the same deduction context)
  const firstTransactionId = selectedTransactionIds[0] || ""
  const count = selectedTransactionIds.length
  const items = `${count} ${itemNoun}${count !== 1 ? "s" : ""}`

  // Monitor for popover elements to detect when they're open
  useEffect(() => {
    if (!open) return

    function checkPopoverOpen() {
      const popoverContent = document.querySelector('[data-radix-popper-content-wrapper]')
      popoverOpenRef.current = !!popoverContent
    }

    const interval = setInterval(checkPopoverOpen, 50)
    return () => clearInterval(interval)
  }, [open])

  function handlePromotionSelect(match: PromotionMatch) {
    setSelectedPromotionId(match.promotion.id)
  }

  async function handleSubmit() {
    if (!selectedPromotionId) {
      return
    }

    setLoading(true)
    try {
      const result = await bulkCreatePromotionMatch(selectedTransactionIds, selectedPromotionId)
      
      if (result.ok) {
        const { updated_count, skipped_count } = result.value.data!
        let message = `Updated ${updated_count} ${itemNoun}${updated_count !== 1 ? "s" : ""}`
        if (skipped_count > 0) {
          message += `. Skipped ${skipped_count} ${itemNoun}${skipped_count !== 1 ? "s" : ""} already matched to a promotion`
        }
        
        toast({
          title: "Success",
          description: message,
        })
        
        setSelectedPromotionId(undefined)
        onOpenChange(false)
        triggerRefreshFlag()
        if (onSuccess) {
          onSuccess()
        }
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error.message || "Failed to update promotion matches",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred",
      })
    } finally {
      setLoading(false)
    }
  }

  function handleCancel() {
    setSelectedPromotionId(undefined)
    onOpenChange(false)
  }

  function handleDialogOpenChange(newOpen: boolean) {
    // If a popover is open, ignore the close request temporarily
    if (!newOpen && popoverOpenRef.current) {
      // Check again after a short delay to see if popover has closed
      setTimeout(() => {
        // Only close if popover is no longer open
        if (!popoverOpenRef.current) {
          onOpenChange(false)
        }
      }, 150)
      return
    }
    
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent 
        className="max-w-2xl"
        onInteractOutside={(e) => {
          // Prevent closing when clicking on popover content (PromotionSelector uses Popover)
          const target = e.target as HTMLElement
          // Check if the click is on a popover or command component
          if (target.closest('[data-radix-popper-content-wrapper]') || 
              target.closest('[role="listbox"]') ||
              target.closest('[cmdk-list]') ||
              target.closest('[cmdk-input]') ||
              target.closest('[cmdk-item]')) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk Match Promotion</DialogTitle>
          <DialogDescription>
            Select a promotion to match to {items} selected. Rows already matched to a promotion
            from the source system are left alone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="bulk-promotion-selector" className="text-sm">Promotion</Label>
            <PromotionSelector
              deductionId={firstTransactionId}
              onSelect={handlePromotionSelect}
              selectedPromotionId={selectedPromotionId}
              searchContextSeed={searchContextSeed}
              className="h-9"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedPromotionId || loading}
          >
            {loading ? "Updating..." : `Match to ${items}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

